/**
 * Runtime Extraction Adapter — glTF/GLB Named Nodes
 *
 * P3 — INIT-2026-Q3-SCENE-HIERARCHY (runtime extraction adapters).
 * Walks the glTF node graph (via the engine's getModelTree() pattern)
 * and builds USN nodes from named nodes.
 *
 * Hierarchy: Model → Named glTF nodes.
 *
 * Detect: layerKind === "gltf" or "glb".
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type {
  TreeNode,
  SceneHierarchyManifest,
  TreeNodeDomain,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type {
  DomainAdapter,
  DomainAdapterContext,
  AdapterResult,
} from "../DomainAdapter";

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function generateUlid(): string {
  let t = Date.now();
  const tsChars: string[] = [];
  for (let i = 0; i < 10; i++) {
    tsChars.unshift(CROCKFORD[t % 32]);
    t = Math.floor(t / 32);
  }
  const randBytes = new Uint8Array(10);
  crypto.getRandomValues(randBytes);
  let rand = "";
  for (let i = 0; i < 10; i++) {
    rand += CROCKFORD[randBytes[i] & 0x1f];
    rand += CROCKFORD[(randBytes[i] >> 5) & 0x07 | ((i < 9 ? randBytes[i + 1] & 0x03 : 0) << 3)];
  }
  return tsChars.join("") + rand.slice(0, 16);
}

function usnMeta(id: string, parentId: string | null) {
  return {
    objectId: id,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    relationships: [] as const,
  };
}

interface GltfNodeDescriptor {
  name: string;
  children?: GltfNodeDescriptor[];
}

function walkGltfNodes(
  gltfNodes: GltfNodeDescriptor[],
  parentId: string,
  layerId: string,
  depth: number,
  nodes: TreeNode[],
  sortStart: number,
): void {
  let sortKey = sortStart;
  for (const gn of gltfNodes) {
    const nodeId = generateUlid();
    const hasChildren = (gn.children?.length ?? 0) > 0;

    nodes.push({
      id: nodeId,
      parentId,
      lor: Math.min(depth + 4, 8),
      label: gn.name || `Unnamed Node`,
      kind: hasChildren ? "group" : "element",
      layerId,
      featureId: null,
      domain: "generic" as TreeNodeDomain,
      standardRef: {
        standard: "glTF:2.0",
        classUri: "",
        nativeId: gn.name,
        propertySets: [],
        linkedDocuments: [],
      },
      metadata: { runtimeExtracted: true, source: "gltf-node-graph" },
      hasChildren,
      sortKey: sortKey++,
      ...usnMeta(nodeId, parentId),
    });

    if (hasChildren) {
      walkGltfNodes(gn.children!, nodeId, layerId, depth + 1, nodes, 0);
    }
  }
}

const GLTF_LAYER_KINDS = new Set(["gltf", "glb", "ifc-glb"]);

export const GltfNodeAdapter: DomainAdapter = {
  domain: "generic" as TreeNodeDomain,
  version: "1.0.0",
  standard: "glTF:2.0",

  detect(item: SceneItem): boolean {
    return GLTF_LAYER_KINDS.has(item.layerKind);
  },

  async buildManifest(
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context: DomainAdapterContext,
  ): Promise<AdapterResult> {
    const nodes: TreeNode[] = [];
    const rootId = generateUlid();

    const root: TreeNode = {
      id: rootId,
      parentId: null,
      lor: 3,
      label: asset.assetNodeId || asset.layerId,
      kind: "asset",
      layerId: asset.layerId,
      featureId: null,
      domain: "generic" as TreeNodeDomain,
      standardRef: null,
      metadata: { runtimeExtracted: true, source: "gltf-node-graph" },
      hasChildren: true,
      sortKey: 0,
      ...usnMeta(rootId, null),
    };
    nodes.push(root);

    if (context.fetchJson) {
      const href =
        item.endpoints["href"] ?? Object.values(item.endpoints)[0] ?? "";
      try {
        const raw = await context.fetchJson(`${href}/model-tree.json`);
        if (Array.isArray(raw)) {
          walkGltfNodes(raw as GltfNodeDescriptor[], rootId, asset.layerId, 0, nodes, 0);
        }
      } catch {
        // Fall through — synthetic root only
      }
    }

    root.hasChildren = nodes.length > 1;

    const manifest: SceneHierarchyManifest = {
      schema: "geosoft.scene-hierarchy/1",
      gtwinId: asset.gtwinId,
      rootLayerId: asset.layerId,
      generatedAt: new Date().toISOString(),
      domain: "generic",
      standardName: "glTF",
      standardVersion: "2.0",
      totalNodes: nodes.length,
      nodeCountByLoR: {},
      nodes,
      paginated: false,
      dataSources: {
        [asset.layerId]: {
          layerId: asset.layerId,
          label: "glTF Model",
          mediaKind: item.layerKind,
          primary: true,
        },
      },
    };

    return { ok: true, manifest };
  },
};
